import GhostContentAPI from "@tryghost/content-api";
import Fuse from 'fuse.js';
import YouTubeSearch from '../youtube-search.js';
import { sendBlogEvent } from './analytics.js';
import { ghostImageUrl, ghostImageSrcset } from '../utils/image.js';

// Narrowed so the index fetch does not drag every post's rendered body along with
// it. Caveat: `fields` also strips Ghost's computed `reading_time` — derived from that
// body — and it cannot be asked back, so results omit the "N min read" line.
const SEARCH_POST_FIELDS = 'title,custom_excerpt,url,feature_image,published_at';

// `fields` strips the computed `primary_tag`, which the index and the result markup
// both read. It is the first public tag, so rebuild it from the included `tags`.
function withPrimaryTag(post) {
  return {
    ...post,
    primary_tag: (post.tags || []).find((tag) => tag.visibility === 'public') || null,
  };
}

class GhostSearch {
  constructor() {
    this.api = null;
    this.fuse = null;
    this.deepFuse = null;
    this.posts = [];
    this.deepPosts = [];
    this.isInitialized = false;
    this.searchInput = null;
    this.resultsContainer = null;
    this.searchTimeout = null;
    this.debounceDelay = 300;
    this.youtubeSearch = new YouTubeSearch();
    this.enableYouTube = false;
    this.isDeep = false;
    this.deepSwitch = null;
    this.postsPromise = null;
    this.deepPostsPromise = null;
    this.fusePromise = null;
    this.deepFusePromise = null;
  }

  async init() {
    // Get API configuration from the page
    const searchContainer = document.querySelector('.search');
    if (!searchContainer) return;

    const apiUrl = window.GhostConfig?.apiUrl || window.location.origin;
    const contentKey = searchContainer.getAttribute('data-content-key') || 
                      document.querySelector('[data-content-key]')?.getAttribute('data-content-key');
    
    if (!contentKey) {
      console.warn('Content API key not found for search');
      return;
    }

    this.api = new GhostContentAPI({
      url: apiUrl,
      key: contentKey,
      version: 'v5.0'
    });

    this.searchInput = searchContainer.querySelector('.search-input');
    this.resultsContainer = searchContainer.querySelector('.search-results__list');
    this.deepSwitch = searchContainer.querySelector('.search-deep-switch');
    
    if (!this.searchInput || !this.resultsContainer) {
      console.warn('Search input or results container not found');
      return;
    }

    // YouTube channel ID — API key lives in the Cloudflare Worker proxy now,
    // not in the client. The init only needs the channel ID for the playlist
    // listing call (/api/youtube/playlists?channelId=...).
    const youtubeChannelId = searchContainer.getAttribute('data-youtube-channel-id');
    if (youtubeChannelId) {
      await this.youtubeSearch.init(youtubeChannelId);
      this.enableYouTube = true;
    }

    this.setupEventListeners();

    // Deep search is off by default; no persistence
    this.isDeep = false;
    if (this.deepSwitch) {
      this.deepSwitch.classList.toggle('is-on', this.isDeep);
    }
    this.isInitialized = true;

    // Don't re-add a preload here — warmIndex() owns when the index gets built.
  }

  setupEventListeners() {
    const wireInput = (input) => {
      input.addEventListener('input', (e) => {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.performSearch(e.target.value.trim());
        }, this.debounceDelay);
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.performSearch(input.value.trim());
        }
      });

      input.addEventListener('focus', () => this.warmIndex(), { once: true });
    };

    // Mobile input (inside .search)
    wireInput(this.searchInput);

    // Desktop input (inside .search-bar)
    const desktopInput = document.querySelector('.search-bar__input');
    if (desktopInput) {
      wireInput(desktopInput);
    }

    // openSearch() focuses an input, but only 50ms later on desktop, and a
    // pointerdown lands before that again — worth the head start on four round trips.
    const openBtn = document.querySelector('.js-search-open');
    if (openBtn) {
      openBtn.addEventListener('pointerdown', () => this.warmIndex(), { once: true });
    }

    // Deep search toggle
    const btn = document.querySelector('.search-deep-switch');
    if (btn) {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        this.isDeep = !this.isDeep;
        btn.classList.toggle('is-on', this.isDeep);
        if (this.isDeep && !this.deepFuse) {
          // Build deep index lazily
          await this.initializeDeepFuse();
        }

        // Re-run current query if present
        const q = this.searchInput.value.trim();
        if (q.length >= 2) {
          this.performSearch(q);
        }
      });
    }
  }

  warmIndex() {
    // Fire-and-forget: performSearch awaits the same promise, so a query typed before
    // this settles reuses the in-flight fetch instead of starting a second.
    this.initializeFuse().catch((error) => {
      console.error('Failed to warm search index:', error);
    });
  }

  // Ghost caps `limit` at 100 whatever you ask for (`limit: 'all'` included), so the
  // archive always has to be walked a page at a time. Page 1 reports how many pages
  // there are, so the remainder go out together rather than as a chain — these are
  // uncached origin round trips and the chain was the bulk of the wait.
  async fetchPosts(options) {
    // Spread first: `filter`, `limit` and `page` drive the loop, and letting a caller
    // pin `page` would leave it requesting page 1 for the life of the tab.
    const browse = (page) => this.api.posts.browse({
      ...options,
      filter: 'tag:-hash-hide',
      limit: 100,
      page,
    });

    const first = await browse(1);
    if (!first || first.length === 0) return [];

    const { pages } = first.meta?.pagination || {};
    if (!pages || pages <= 1) return [...first];

    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, index) => browse(index + 2))
    );

    return rest.reduce((all, page) => all.concat(page || []), [...first]);
  }

  loadPosts() {
    if (this.posts.length > 0) return Promise.resolve(this.posts);

    // Hold the promise, not just the result: both inputs and performSearch can ask
    // for the index before the first fetch settles, and each miss would otherwise
    // start its own pass over the archive.
    if (!this.postsPromise) {
      this.postsPromise = this.fetchPosts({
        include: ['tags'],
        fields: SEARCH_POST_FIELDS,
      })
        .then((posts) => {
          // Only a non-empty archive is worth keeping. Ghost can answer 200 with an
          // empty list while reindexing, and pinning that would wedge search until
          // the visitor reloaded the page.
          if (posts.length === 0) this.postsPromise = null;
          this.posts = posts.map(withPrimaryTag);
          return this.posts;
        })
        .catch((error) => {
          console.error('Failed to load posts for search:', error);
          this.postsPromise = null; // let the next search retry
          return [];
        });
    }

    return this.postsPromise;
  }

  loadDeepPosts() {
    if (this.deepPosts.length > 0) return Promise.resolve(this.deepPosts);

    if (!this.deepPostsPromise) {
      // Deep search matches article bodies, so `plaintext` is the point of this fetch.
      this.deepPostsPromise = this.fetchPosts({
        include: ['tags'],
        fields: SEARCH_POST_FIELDS,
        formats: ['plaintext'],
      })
        .then((posts) => {
          if (posts.length === 0) this.deepPostsPromise = null;
          this.deepPosts = posts.map((post) => ({
            ...withPrimaryTag(post),
            plaintext: typeof post.plaintext === 'string' ? post.plaintext : '',
          }));
          return this.deepPosts;
        })
        .catch((error) => {
          console.error('Failed to load deep posts for search:', error);
          this.deepPostsPromise = null; // let the next toggle retry
          return [];
        });
    }

    return this.deepPostsPromise;
  }

  initializeFuse() {
    if (this.fuse) return Promise.resolve(this.fuse);

    // Guard the build as well as the fetch: warmIndex and a fast first keystroke both
    // arrive with this.fuse still null, and would each construct an index.
    if (this.fusePromise) return this.fusePromise;

    this.fusePromise = this.loadPosts().then((posts) => {
      if (posts.length === 0) {
        this.fusePromise = null;
        return null;
      }

      this.fuse = new Fuse(posts, {
        includeScore: true,
        minMatchCharLength: 3,
        threshold: 0.4,
        ignoreLocation: true,
        keys: [
          { name: 'title', weight: 0.6 },
          { name: 'custom_excerpt', weight: 0.3 },
          { name: 'primary_tag.name', weight: 0.1 }
        ]
      });

      return this.fuse;
    });

    return this.fusePromise;
  }

  initializeDeepFuse() {
    if (this.deepFuse) return Promise.resolve(this.deepFuse);
    if (this.deepFusePromise) return this.deepFusePromise;

    this.deepFusePromise = this.loadDeepPosts().then((posts) => {
      if (posts.length === 0) {
        this.deepFusePromise = null;
        return null;
      }

      this.deepFuse = new Fuse(posts, {
        includeScore: true,
        minMatchCharLength: 3,
        ignoreLocation: true,
        findAllMatches: true,
        threshold: 0.4,
        keys: [
          { name: 'title', weight: 0.5 },
          { name: 'custom_excerpt', weight: 0.2 },
          { name: 'primary_tag.name', weight: 0.05 },
          { name: 'plaintext', weight: 0.25 }
        ]
      });

      return this.deepFuse;
    });

    return this.deepFusePromise;
  }

  showSearchPopup() {
    const popup = document.querySelector('.search');
    if (popup && !popup.classList.contains('show')) {
      popup.classList.add('show');
    }
  }

  hideSearchPopup() {
    const popup = document.querySelector('.search');
    if (popup) popup.classList.remove('show');
  }

  async performSearch(query) {
    if (!this.isInitialized) return;

    if (!query || query.length < 2) {
      this.showEmptyState();
      return;
    }

    if (window.innerWidth > 900) {
      this.showSearchPopup();
    }

    this.currentQuery = query;
    this.showLoadingState();

    try {
      if (this.enableYouTube) {
        await this.performCombinedSearch(query);
      } else {
        const fuse = this.isDeep ? await this.initializeDeepFuse() : await this.initializeFuse();
        if (!fuse) {
          this.showErrorState();
          return;
        }

        const results = fuse.search(query, { limit: 10 });
        this.displayResults(results.map(result => result.item));
      }
    } catch (error) {
      console.error('Search failed:', error);
      this.showErrorState();
    }
  }

  showLoadingState() {
    this.showResults();
    this.resultsContainer.innerHTML = `
      <div class="search-loading">
        <div class="search-loading__spinner"></div>
        <p>検索中...</p>
      </div>
    `;
  }

  showEmptyState() {
    if (window.innerWidth > 900) {
      const popup = document.querySelector('.search');
      if (popup && popup.classList.contains('show')) {
        this.showResults();
        this.resultsContainer.innerHTML = `
          <div class="search-hint">
            <p>キーワードを入力すると、検索結果が表示されます。</p>
          </div>
        `;
        return;
      }
    }
    this.hideResults();
  }

  showResults() {
    const resultsContainer = document.querySelector('.search-results');
    if (resultsContainer) {
      resultsContainer.style.display = 'block';
      // Trigger animation after a small delay to ensure display is set
      setTimeout(() => {
        resultsContainer.classList.add('show');
      }, 10);
    }
  }

  hideResults() {
    const resultsContainer = document.querySelector('.search-results');
    if (resultsContainer) {
      resultsContainer.classList.remove('show');
      // Hide after animation completes
      setTimeout(() => {
        resultsContainer.style.display = 'none';
      }, 300);
    }
  }

  showErrorState() {
    this.showResults();
    this.resultsContainer.innerHTML = `
      <div class="search-error">
        <p>検索が一時的に利用できません。時間をおいて再度お試しください。</p>
      </div>
    `;
  }

  trackSearch(count) {
    sendBlogEvent('search', {
      search_term: this.currentQuery || '',
      result_count: count
    });
  }

  displayResults(posts) {
    if (posts.length === 0) {
      this.showResults();
      this.resultsContainer.innerHTML = `
        <div class="search-no-results">
          <p>検索条件に一致する記事は見つかりませんでした。</p>
        </div>
      `;
      this.trackSearch(0);
      return;
    }

    this.showResults();
    const html = posts.map(post => this.renderSearchResult(post)).join('');
    this.resultsContainer.innerHTML = html;
    this.trackSearch(posts.length);
  }

  renderSearchResult(post) {
    const featureImage = post.feature_image ?
      `<div class="search-results-item__image">
        <img
          src="${ghostImageUrl(post.feature_image, 300)}"
          srcset="${ghostImageSrcset(post.feature_image, [300, 600])}"
          sizes="275px"
          alt="${post.title}"
        >
      </div>` : '';

    const primaryTag = post.primary_tag ?
      `<span class="search-results-item__tag">${post.primary_tag.name}</span>` : '';

    const readingTime = post.reading_time ?
      `<span class="search-results-item__reading-time">${post.reading_time}分で読めます</span>` : '';

    const date = new Date(post.published_at);
    const dateStr = date.toLocaleDateString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });

    const tagMobile = primaryTag ? primaryTag.replace('class="search-results-item__tag"', 'class="search-results-item__tag search-results-item__tag--mobile"') : '';
    const tagDesktop = primaryTag ? primaryTag.replace('class="search-results-item__tag"', 'class="search-results-item__tag search-results-item__tag--desktop"') : '';

    return `
      <a href="${post.url}" class="search-results-item flex items-center">
        <div class="search-results-item__main flex direction-column">
          ${tagMobile}
          <h3 class="search-results-item__title">${post.title}</h3>
          <div class="search-results-item__meta flex items-center">
            ${tagDesktop}
            <span class="search-results-item__date-group">
              <time datetime="${date.toISOString().split('T')[0]}" class="search-results-item__date">${dateStr}</time>
              ${readingTime}
            </span>
          </div>
        </div>
        ${featureImage}
      </a>
    `;
  }

  // Future method for YouTube integration
  async searchYouTube(query) {
    // Placeholder for future YouTube API integration
    console.log('YouTube search not implemented yet:', query);
    return [];
  }

  // Method to combine Ghost and YouTube results
  async performCombinedSearch(query) {
    try {
      // Get Ghost posts
      const fuse = await this.initializeFuse();
      let ghostResults = [];
      if (fuse) {
        const fuseResults = fuse.search(query, { limit: 5 });
        ghostResults = fuseResults.map(result => ({ ...result.item, type: 'ghost' }));
      }

      // Get YouTube videos (failure must not block Ghost results)
      let formattedYoutubeResults = [];
      try {
        const youtubeResults = await this.youtubeSearch.search(query, { maxResults: 5 });
        formattedYoutubeResults = youtubeResults.map(video => ({ ...video, type: 'youtube' }));
      } catch (ytError) {
        console.warn('YouTube search failed, showing Ghost results only:', ytError);
      }

      // Combine and sort results by relevance
      const allResults = [...ghostResults, ...formattedYoutubeResults];
      
      // Simple sorting: Ghost posts first, then YouTube videos
      allResults.sort((a, b) => {
        if (a.type === 'ghost' && b.type === 'youtube') return -1;
        if (a.type === 'youtube' && b.type === 'ghost') return 1;
        return 0;
      });

      this.displayCombinedResults(allResults);
    } catch (error) {
      console.error('Combined search failed:', error);
      this.showErrorState();
    }
  }

  displayCombinedResults(results) {
    if (results.length === 0) {
      this.showResults();
      this.resultsContainer.innerHTML = `
        <div class="search-no-results">
          <p>検索条件に一致する記事・動画は見つかりませんでした。</p>
        </div>
      `;
      this.trackSearch(0);
      return;
    }

    this.showResults();
    const html = results.map(item => {
      if (item.type === 'youtube') {
        return this.youtubeSearch.renderSearchResult(item);
      } else {
        return this.renderSearchResult(item);
      }
    }).join('');

    this.resultsContainer.innerHTML = html;
    this.trackSearch(results.length);
  }
}

// Initialize search when DOM is ready
export function initSearch() {
  // Only initialize if search popup exists
  const searchPopup = document.querySelector('.search');
  if (!searchPopup) return;

  const search = new GhostSearch();
  search.init();
}

// Global search bar buttons -> open/close search popup
export function initSearchBarButton() {
  const openBtn = document.querySelector('.js-search-open');
  const searchBar = document.querySelector('.search-bar');
  const searchPopup = document.querySelector('.search');

  if (!openBtn || !searchPopup) return;

  const desktopInput = document.querySelector('.search-bar__input');
  const mobileInput = searchPopup.querySelector('.search-input');

  function isDesktop() {
    return window.innerWidth > 900;
  }

  function openSearch() {
    if (isDesktop()) {
      // Desktop: only expand the bar, show popup only after user types
      searchBar?.classList.add('is-open');
      setTimeout(() => {
        try { desktopInput?.focus(); } catch (e) {}
      }, 50);
    } else {
      searchPopup.classList.add('show');
      document.body.classList.add('search-popup-active');
      document.dispatchEvent(new Event('search-opened'));
      try { mobileInput?.focus(); } catch (e) {}
    }
  }

  function closeSearch() {
    searchPopup.classList.remove('show');
    document.body.classList.remove('search-popup-active');
    searchBar?.classList.remove('is-open');
    if (desktopInput) desktopInput.value = '';
    if (mobileInput) mobileInput.value = '';
  }

  openBtn.addEventListener('click', (e) => {
    e.preventDefault();
    if (searchPopup.classList.contains('show')) {
      closeSearch();
    } else {
      openSearch();
    }
  });

  document.addEventListener('click', (e) => {
    if (window.innerWidth <= 900) return;
    if (!searchPopup.classList.contains('show')) return;
    if (searchBar?.contains(e.target) || searchPopup.contains(e.target)) return;
    closeSearch();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && searchPopup.classList.contains('show')) {
      closeSearch();
    }
  });

  document.addEventListener('menu-opened', () => {
    if (window.innerWidth <= 900) closeSearch();
  });
}

